import { errorMessage, type ApiClient, type ApiErrorBody } from "@planner/core";
import { useEffect, useState, type ReactNode } from "react";
import { PasswordField } from "../components/PasswordField.js";
import { useLocale } from "../i18n/provider.js";
import { platform } from "../platform/index.js";

/**
 * **التحقّقُ الثاني — يُطلَب في موضعٍ واحد، لا في كل شاشة.**
 *
 * فالبلوبرنت §8 يُلزم تحقّقاً ثانياً قبل **ثماني عمليات كبيرة** موزّعة على ستّ
 * شاشات: حذفُ عميل · حذفُ موظّف · مصفوفةُ دور · استثناءٌ فرديّ · حذفٌ من السجلّ
 * · إنهاءُ كل جلسات أحدٍ · تصديرُ بيانات عميل · **وتعديلُ إعدادات النظام**. ولو
 * كُتب في كلٍّ منها لنُسي في واحدة — وهي بالضبط التي ستُستعمَل.
 *
 * والكتالوجُ يَعِد: «يتحقّق المستخدم مرة ثانية ويكمل. **ليس عطباً**». **ولم يكن
 * لذلك مسارٌ ولا شاشة**: `lastVerifiedAt` لا يُكتب إلا عند الدخول، فمن مضت على
 * دخوله أربعُ ساعات ليس أمامه إلا الخروجُ والعودة — فيفقد ما كتب.
 *
 * ويُعترَض من **مضيف التوست**: كلُّ فشلٍ في النظام يمرّ به، فلا موضعَ ثانٍ
 * يُنسى. ولأن المضيف فوق التطبيق والعميلُ يُولَد تحته، فالحالةُ هنا **مخزنٌ
 * صغير لا سياقُ React** — وهو مفردٌ على كل حال.
 */

type Waiting = {
  readonly operation: string;
  /** **يُعاد النداءُ نفسُه بعد التحقّق** — فلا يُعيد المستخدم كتابةَ شيء */
  readonly retry: (() => void) | null;
};

let waiting: Waiting | null = null;
let listener: ((next: Waiting | null) => void) | null = null;

function publish(next: Waiting | null): void {
  waiting = next;
  listener?.(next);
}

/**
 * **يُسأل عند كل فشل**: هل هذا طلبُ تحقّقٍ ثانٍ؟
 *
 * فإن كان، فُتحت النافذةُ وأُعيد `true` — **فلا توست**، إذ ليس عطباً. وإلا
 * `false` ويمضي الفشلُ إلى التوست بكوده ومرجعه كالعادة.
 */
export function interceptStepUp(error: ApiErrorBody, retry?: () => void): boolean {
  if (error.code !== "STEP_UP_REQUIRED") return false;
  const operation = error.context?.["operation"];
  publish({
    operation: typeof operation === "string" ? operation : "",
    retry: retry ?? null,
  });
  return true;
}

/**
 * **مضيفُ النافذة** — يُركَّب مرّةً داخل التطبيق حيث يوجد عميلُ الشبكة.
 */
export function StepUpHost({ client }: { readonly client: ApiClient }): ReactNode {
  const [current, setCurrent] = useState<Waiting | null>(waiting);

  useEffect(() => {
    listener = setCurrent;
    return () => {
      listener = null;
    };
  }, []);

  if (current === null) return null;
  return (
    <StepUpDialog
      client={client}
      hasRetry={current.retry !== null}
      onCancel={() => {
        publish(null);
      }}
      onVerified={() => {
        const { retry } = current;
        publish(null);
        retry?.();
      }}
    />
  );
}

function StepUpDialog({
  client,
  hasRetry,
  onCancel,
  onVerified,
}: {
  readonly client: ApiClient;
  readonly hasRetry: boolean;
  readonly onCancel: () => void;
  readonly onVerified: () => void;
}): ReactNode {
  const { t, locale } = useLocale();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiErrorBody | null>(null);

  async function submit(): Promise<void> {
    if (busy || password === "") return;
    setBusy(true);
    setError(null);
    const result = await client.request("/auth/verify", {
      method: "POST",
      idempotencyKey: platform.newId(),
      body: { password },
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setPassword("");
    onVerified();
  }

  return (
    <div className="mod stepup" role="dialog" aria-modal="true" aria-label={t("stepUp.title")}>
      <div className="mod-head">
        <b>{t("stepUp.title")}</b>
        <button
          data-close
          type="button"
          className="icb"
          aria-label={t("common.close")}
          onClick={onCancel}
        >
          ×
        </button>
      </div>
      <div className="confirm-body">
        {/** **وسببُ الطلب يُقال** — فلا يظنّها المستخدم عطباً أو شكّاً فيه */}
        <p className="confirm-what">{t("stepUp.why")}</p>
        {/**
         * **ويُقال ماذا يحدث بعده** — فمن لا يُعاد نداؤه تلقائياً يعرف أن
         * عليه الضغطَ ثانية، ولا ينتظر شيئاً لن يأتي.
         */}
        <p className="field-hint">{t(hasRetry ? "stepUp.thenRetries" : "stepUp.thenPressAgain")}</p>

        {error ? (
          <div className="signin-error" role="alert">
            <p>{errorMessage(locale, error)}</p>
            <div className="codes">
              <span className="code">{error.code}</span>
              <span className="code">{error.correlationId}</span>
            </div>
          </div>
        ) : null}

        <PasswordField
          value={password}
          onChange={setPassword}
          label={t("stepUp.password")}
          invalid={error !== null}
          required
        />

        <div className="mod-actions">
          <button type="button" className="btn gh" onClick={onCancel} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || password === ""}
            onClick={() => {
              void submit();
            }}
          >
            {t(busy ? "common.working" : "stepUp.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
